// src/components/BuyButton.jsx
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";

export default function BuyButton({
  product = { name: "Dope Shot", price: 36, image: "/dopeshoot-model3.webp" },
  className = "buy-btn",
  children = "Buy now",
  packSize = 1 // 👈 per purchase includes 3 pieces
}) {
  const [open, setOpen] = useState(false);
  const [packs, setPacks] = useState(1); // qty = number of packs
  const shellRef = useRef(null);
  const navigate = useNavigate();

  // lock scroll + animate in
  useEffect(() => {
    if (!open) return;
    const el = shellRef.current;
    // lock page scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Animate modal in (existing logic)
    gsap.set(el, { autoAlpha: 0 });
    gsap.set(".bm-card", { y: 20, scale: 0.98, autoAlpha: 0 });
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
    tl.to(el, { autoAlpha: 1, duration: 0.15 })
      .to(".bm-card", { y: 0, scale: 1, autoAlpha: 1, duration: 0.3 }, "<");
    // Escape key closes modal
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const inc = () => setPacks((n) => Math.min(99, n + 1));
  const dec = () => setPacks((n) => Math.max(1, n - 1));

  const subtotal = (product.price * packs).toFixed(2);
  const totalPieces = packs * packSize;

  const checkout = () => {
    // Always include Stripe Price ID for checkout
    const productWithPriceId = {
      ...product,
      priceId: product.priceId || "price_1SIOYoQ2jusiVrGtTNV7AmnQ" // fallback to your default Price ID
    };
    navigate("/checkout", { state: { product: productWithPriceId, packs, packSize } });
    setOpen(false);
  };

  // Patch: Ensure close button is always visible and clickable
  useEffect(() => {
    if (!open) return;
    // Try both .bm-close and .buy-modal-close for compatibility
    const closeBtn = shellRef.current?.querySelector('.bm-close, .buy-modal-close');
    if (closeBtn) {
      closeBtn.style.zIndex = '10001';
      closeBtn.style.position = 'absolute';
      closeBtn.style.top = '12px';
      closeBtn.style.right = '12px';
    }
  }, [open]);
  return (
    <>
      <button className={className} onClick={() => setOpen(true)}>
        {children}
      </button>

      {!open
        ? null
        : createPortal(
            <div
              className="bm-backdrop"
              ref={shellRef}
              onClick={(e) => e.target === e.currentTarget && setOpen(false)}
            >
              <div className="bm-card" role="dialog" aria-modal="true" aria-label={`Buy ${product.name}`}>
                <button className="bm-close" onClick={() => setOpen(false)} aria-label="Close">×</button>

                <div className="bm-body">
                  <img className="bm-img" src={product.image} alt={product.name} />
                  <div className="bm-info">
                    <h3 className="bm-name">{product.name}</h3>
                    <p className="bm-price">${product.price.toFixed(2)} <span style={{color:"var(--muted)", fontWeight:600}}>/ pack</span></p>

                    <div className="bm-packline">
                      {/*Each pack includes <strong>{packSize}</strong> pieces.*/}
                    </div>

                    <div className="bm-qty">
                      <span>Packs</span>
                      <div className="qty-ctrl">
                        <button onClick={dec} aria-label="Decrease">–</button>
                        <input
                          value={packs}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, "");
                            const n = Math.max(1, Math.min(99, Number(v || "1")));
                            setPacks(n);
                          }}
                          inputMode="numeric"
                          aria-label="Packs"
                        />
                        <button onClick={inc} aria-label="Increase">+</button>
                      </div>
                    </div>

                    <div className="bm-pieces">
                      <span>Total pieces</span>
                      <strong>{totalPieces}</strong>
                    </div>

                    <div className="bm-sub">
                      <span>Subtotal</span>
                      <strong>${subtotal}</strong>
                    </div>

                    <button className="bm-checkout" onClick={checkout}>PLACE ORDER</button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}
    </>
  );
}
